import { FieldValues, UseFormWatch } from 'react-hook-form';
import { IRenderTemplateRow } from './types';

export const shouldRenderEmptyComponent = (dependsOn: any, dependentValue: any, hasValidOrDependency: boolean, hasValidAndDependency: boolean) => {
  if (!dependsOn) return false;

  return (
    (dependsOn?.hasOwnProperty('value') && dependentValue !== dependsOn.value) ||
    (dependsOn?.hasOwnProperty('equals') && dependentValue !== dependsOn.equals) ||
    (dependsOn?.hasOwnProperty('includes') && !dependentValue?.includes(dependsOn.includes)) ||
    (dependsOn?.hasOwnProperty('or') && !hasValidOrDependency) ||
    (dependsOn?.hasOwnProperty('and') && !hasValidAndDependency)
  );
};

export const evaluateDependencyItem = (dependentValue: any, item: any): boolean => {
  if (item?.hasOwnProperty('value')) {
    return dependentValue === item.value;
  }
  if (item?.hasOwnProperty('equals')) {
    return dependentValue === item.equals;
  }
  if (item?.hasOwnProperty('includes')) {
    return dependentValue?.includes(item.includes);
  }
  return false;
};

/**
 * Checks if all repeated components with the same name fail the dependsOn condition check
 *
 * @param rows - All rows in the rendering template page
 * @param componentName - Component name of the element in the rendering template row that is potentially repeated for complex conditional rendering cases
 * @param pageName - Rendering template page name to reference form data values
 * @param hookFormWatch - Watch passed in from react component calling this function
 * @returns Boolean indicating if all repeated components fail their dependsOn checks indicating it's safe to unregister form values
 */
export const allRepeatedComponentsFailDependsOnCheck = (
  rows: IRenderTemplateRow[],
  componentName: string,
  pageName: string,
  hookFormWatch: UseFormWatch<FieldValues>
): boolean => {
  const rowsWithComponentName = rows.filter((row) => row.name === componentName);
  const hasRepeatedComponentNameInRows = rowsWithComponentName?.length > 1;
  // no repeated component name in rows, so no need to check multiple component dependsOn conditions safe to unregister
  if (!hasRepeatedComponentNameInRows) return true;

  const dependsOnResults = rowsWithComponentName.map((row) => {
    const { dependsOn } = row;
    const dependentValue = dependsOn ? hookFormWatch(`${pageName}.${dependsOn.name}`) : null;
    const hasValidOrDependency: boolean = dependsOn?.or
      ? dependsOn.or.some((item: any) => {
          const dependentValue = hookFormWatch(`${pageName}.${item.name}`);
          return evaluateDependencyItem(dependentValue, item);
        })
      : null;
    const hasValidAndDependency: boolean = dependsOn?.and
      ? dependsOn.and.every((item: any) => {
          const dependentValue = hookFormWatch(`${pageName}.${item.name}`);
          return evaluateDependencyItem(dependentValue, item);
        })
      : null;

    return shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency);
  });

  // if all results are true, then all repeated components failed dependsOn check, so we can unregister form values
  return dependsOnResults.every((result: boolean) => result === true);
};
